import React from "react"
import {
  Flex, Box, Text, Checkbox,
} from "@chakra-ui/react"
import { GuestSummary } from "sharedTypes"
import GuestAvatar from "components/elements/GuestAvatar"
import GroupLabel from "components/GuestList/GroupLabel"
import { LAST_NAME_ORDER_PREFIX } from "utilities/constants"
import { ExtendedGuestSummary } from "../helpers"

type Props = {
  guest: ExtendedGuestSummary;
  multipleSelect: boolean;
  selectedGuestIdsSet: Set<string>;
  allSelected: boolean;
  disableMainGuests: boolean;
  disabledGuestIds: string[];
  order: string;
  onSelectionChange: (
    event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>,
    guest: GuestSummary,
    checked?: boolean,
  ) => void;
}

const NameCell = ({
  guest,
  multipleSelect,
  selectedGuestIdsSet,
  allSelected,
  disableMainGuests,
  disabledGuestIds,
  order,
  onSelectionChange,
}: Props) => {
  const { firstName, lastName, photoUrl } = guest.personInfo
  const {
    status, checkedIn, groupSize,
  } = guest
  const isIndented = !!guest.isPlusOne
  const isPlusOne = !guest.plusOnes?.length && groupSize > 1
  const isDisabled = multipleSelect && (
    (disableMainGuests && (guest.plusOnes?.length ?? 0) > 0)
    || disabledGuestIds.includes(guest.id)
  )

  return (
    <Flex align="center" width="100%">
      {isIndented && <Box w={5} flexShrink={0} />}
      {multipleSelect && (
        <Flex
          align="center"
          justify="center"
          position="relative"
          mr={2}
          w={8}
          h={8}
          flexShrink={0}
          boxSizing="content-box"
          opacity={isDisabled ? 0.3 : 1}
          _hover={{ opacity: 1 }}
          transitionProperty="opacity"
          transitionTimingFunction="cubic-bezier(0.4, 0, 0.2, 1)"
          transitionDuration="200ms"
          cursor={isDisabled ? "default" : "pointer"}
          onClick={(e) => {
            e.stopPropagation()
            if (!isDisabled) {
              const isCurrentlySelected = allSelected || selectedGuestIdsSet.has(guest.id)
              onSelectionChange(e, guest, !isCurrentlySelected)
            }
          }}
        >
          <Checkbox
            p={2}
            isChecked={allSelected || selectedGuestIdsSet.has(guest.id)}
            isDisabled={isDisabled}
            readOnly
            pointerEvents="none"
          />
        </Flex>
      )}
      <Box mr={2} flexShrink={0}>
        <GuestAvatar
          firstName={firstName}
          lastName={lastName}
          photoUrl={photoUrl}
          status={status}
          checkedIn={!!checkedIn}
          size={8}
        />
      </Box>
      <Flex align="center" minW={0} flex={1} overflow="hidden">
        <Text
          as="span"
          isTruncated
          noOfLines={1}
          minW={0}
          mr={2}
          whiteSpace="nowrap"
        >
          {order?.startsWith(LAST_NAME_ORDER_PREFIX) ? (
            <>
              <Box as="span" fontWeight="bold">
                {lastName}
              </Box>
              {lastName ? ", " : ""}
              {firstName}
            </>
          ) : (
            <>
              <Box as="span" fontWeight="bold">
                {firstName}
              </Box>{" "}
              {lastName}
            </>
          )}
        </Text>

        {groupSize > 1 && (
        <Box flexShrink={0}>
          <GroupLabel groupSize={groupSize} childGuest={isPlusOne} />
        </Box>
        )}
      </Flex>
    </Flex>
  )
}

export default NameCell
